import React, { useCallback, useEffect, useState } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Tag, 
  Space, 
  Input, 
  Select, 
  message, 
  Modal, 
  Form, 
  InputNumber,
  Popconfirm,
  Row,
  Col,
  Statistic,
} from 'antd';
import { 
  HeartOutlined, 
  ExclamationCircleOutlined, 
  ReloadOutlined,
  PlusOutlined,
  SearchOutlined,
  DeleteOutlined,
  UpCircleOutlined,
  StopOutlined,
  ClearOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { 
  fetchGpuList, 
  updateGpuHeartbeat, 
  markGpuAsFaulty, 
  recoverGpu,
  registerGpu,
  unregisterGpu,
  setFilters,
  setGpuUpgradeEnabled,
  clearGpuUpgradeStatus,
  getGpuUpgradeStatus,
} from '../../store/slices/gpuSlice';
import { fetchTaskTypes } from '../../store/slices/taskSlice';
import { monitoringApi } from '../../services/monitoring';
import { GpuClusterDetail, GpuNodeInfo } from '../../types';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const { Option } = Select;
const GPU_LAST_TASK_WARNING_MS = 10 * 60 * 1000;
const DISPLAY_TIME_ZONE = 'Asia/Shanghai';
const SHANGHAI_UTC_OFFSET = '+08:00';
const timeFormatter = new Intl.DateTimeFormat('zh-CN', {
  timeZone: DISPLAY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const normalizeTimestamp = (value?: number | string | null): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return null;
    }
    return value < 1_000_000_000_000 ? value * 1000 : value;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  if (/^\d+$/.test(trimmedValue)) {
    const numericValue = Number(trimmedValue);
    return numericValue < 1_000_000_000_000 ? numericValue * 1000 : numericValue;
  }

  const normalizedValue = trimmedValue.includes('T')
    ? trimmedValue
    : trimmedValue.replace(' ', 'T');
  const hasExplicitTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedValue);
  const valueWithDateTime = normalizedValue.includes('T')
    ? normalizedValue
    : `${normalizedValue}T00:00:00`;
  const valueWithTimeZone = hasExplicitTimeZone
    ? valueWithDateTime
    : `${valueWithDateTime}${SHANGHAI_UTC_OFFSET}`;
  const parsedValue = Date.parse(valueWithTimeZone);

  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const formatTimestampInShanghai = (value?: number | string | null) => {
  const timestamp = normalizeTimestamp(value);

  if (!timestamp) {
    return '-';
  }

  return `${timeFormatter.format(new Date(timestamp))} 北京时间`;
};

const formatElapsedTime = (elapsedMs: number) => {
  if (elapsedMs < 60 * 1000) {
    return '刚刚';
  }

  const totalMinutes = Math.floor(elapsedMs / (60 * 1000));

  if (totalMinutes < 60) {
    return `${totalMinutes}分钟前`;
  }

  const totalHours = Math.floor(totalMinutes / 60);

  if (totalHours < 24) {
    const minutes = totalMinutes % 60;
    return minutes > 0 ? `${totalHours}小时${minutes}分钟前` : `${totalHours}小时前`;
  }

  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return hours > 0 ? `${days}天${hours}小时前` : `${days}天前`;
};

const GpuManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { gpuList, loading, filters } = useSelector((state: RootState) => state.gpu);
  const { taskTypes } = useSelector((state: RootState) => state.task);
  const { list: taskVersions } = useSelector((state: RootState) => state.taskVersion);
  const { autoRefresh, refreshInterval } = useSelector((state: RootState) => state.settings);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());
  const [now, setNow] = useState(Date.now());
  const [lastTaskCompletedAtByGpu, setLastTaskCompletedAtByGpu] = useState<
    Record<string, GpuClusterDetail['lastTaskCompletedAt']>
  >({});
  const [form] = Form.useForm();

  const refreshData = useCallback(() => {
    dispatch(fetchGpuList());
    monitoringApi.getGpuClusterStats()
      .then((response) => {
        const nextLastTaskMap = response.data.gpuDetails.reduce<
          Record<string, GpuClusterDetail['lastTaskCompletedAt']>
        >((map, gpuDetail) => {
          map[gpuDetail.gpuId] = gpuDetail.lastTaskCompletedAt;
          return map;
        }, {});

        setLastTaskCompletedAtByGpu(nextLastTaskMap);
      })
      .catch(() => {
        setLastTaskCompletedAtByGpu({});
      });
  }, [dispatch]);

  const getLastTaskCompletedAt = useCallback((gpu: GpuNodeInfo) => (
    gpu.lastTaskCompletedAt ?? lastTaskCompletedAtByGpu[gpu.gpuId] ?? null
  ), [lastTaskCompletedAtByGpu]);

  const isLastTaskOlderThanThreshold = useCallback((gpu: GpuNodeInfo) => {
    const lastTaskCompletedAt = normalizeTimestamp(getLastTaskCompletedAt(gpu));

    return Boolean(
      lastTaskCompletedAt &&
      now - lastTaskCompletedAt > GPU_LAST_TASK_WARNING_MS
    );
  }, [getLastTaskCompletedAt, now]);

  // 注意：由于后端已经在GPU列表接口中返回了 needUpgrade 和 upgradeEnabled 字段
  // 这里不再需要单独调用 getGpuUpgradeStatus 来获取 upgradeEnabled
  // 但如果需要实时更新 upgradeEnabled（例如在其他地方修改后），可以保留此逻辑
  // 由于 getGpuUpgradeStatus 只更新 upgradeEnabled，不会覆盖 needUpgrade，所以是安全的
  useEffect(() => {
    if (gpuList.length > 0 && !loading) {
      // 并行获取所有GPU的升级权限状态（upgradeEnabled）
      // 注意：needUpgrade 已经从列表接口获取，这里只更新 upgradeEnabled
      gpuList.forEach(gpu => {
        dispatch(getGpuUpgradeStatus(gpu.gpuId)).catch(() => {
          // 忽略单个GPU升级状态获取失败
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpuList.length]);

  // 自动刷新
  useAutoRefresh({
    interval: refreshInterval,
    enabled: autoRefresh,
    onRefresh: refreshData,
  });

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 30 * 1000);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    refreshData();
    dispatch(fetchTaskTypes());
  }, [dispatch, refreshData]);

  const handleHeartbeat = async (gpuId: string) => {
    try {
      await dispatch(updateGpuHeartbeat(gpuId)).unwrap();
      message.success('心跳更新成功');
    } catch (error) {
      message.error('心跳更新失败');
    }
  };

  const handleMarkFaulty = async (gpuId: string) => {
    try {
      await dispatch(markGpuAsFaulty({ gpuId, reason: '手动标记' })).unwrap();
      message.success('GPU已标记为故障');
    } catch (error) {
      message.error('标记失败');
    }
  };

  const handleRecover = async (gpuId: string) => {
    try {
      await dispatch(recoverGpu(gpuId)).unwrap();
      message.success('GPU恢复成功');
    } catch (error) {
      message.error('恢复失败');
    }
  };

  const handleUnregister = async (gpuId: string) => {
    try {
      await dispatch(unregisterGpu(gpuId)).unwrap();
      message.success('GPU注销成功');
      refreshData(); // 重新更新列表
    } catch (error) {
      message.error('注销失败');
    }
  };

  const handleSetUpgradeEnabled = async (gpuId: string, enabled: boolean) => {
    try {
      await dispatch(setGpuUpgradeEnabled({ gpuId, enabled })).unwrap();
      message.success(`GPU升级状态已${enabled ? '启用' : '禁用'}`);
      refreshData();
    } catch (error) {
      message.error('设置升级状态失败');
    }
  };

  const handleClearUpgradeStatus = async (gpuId: string) => {
    try {
      await dispatch(clearGpuUpgradeStatus(gpuId)).unwrap();
      message.success('GPU升级状态已清除');
      refreshData();
    } catch (error) {
      message.error('清除升级状态失败');
    }
  };

  const handleRegister = async (values: any) => {
    try {
      await dispatch(registerGpu(values)).unwrap();
      message.success('GPU注册成功');
      setIsModalVisible(false);
      form.resetFields();
      refreshData();
    } catch (error) {
      message.error('注册失败');
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap = {
      ACTIVE: { color: 'green', text: '活跃' },
      OFFLINE: { color: 'orange', text: '离线' },
      FAULTY: { color: 'red', text: '故障' },
    };
    const statusInfo = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  const getPriorityTag = (priority: string) => {
    const priorityMap = {
      VIP: { color: 'gold', text: 'VIP' },
      NORMAL: { color: 'blue', text: '普通' },
    };
    const priorityInfo = priorityMap[priority as keyof typeof priorityMap] || { color: 'default', text: priority };
    return <Tag color={priorityInfo.color}>{priorityInfo.text}</Tag>;
  };

  // 筛选后的数据
  const filteredData = gpuList.filter(gpu => {
    const matchesSearch = !searchText || 
      gpu.gpuId.toLowerCase().includes(searchText.toLowerCase()) ||
      gpu.vastInsId?.toLowerCase().includes(searchText.toLowerCase()) ||
      gpu.ip.includes(searchText);
    
    const matchesStatus = !filters.status || gpu.status === filters.status;
    const matchesTaskType = !filters.taskType || gpu.supportedTaskTypes.includes(filters.taskType);
    const matchesPriority = !filters.priority || gpu.supportedTaskPriority === filters.priority;
    
    return matchesSearch && matchesStatus && matchesTaskType && matchesPriority;
  });

  // 统计信息
  const stats = {
    total: gpuList.length,
    active: gpuList.filter(g => g.status === 'ACTIVE').length,
    offline: gpuList.filter(g => g.status === 'OFFLINE').length,
    faulty: gpuList.filter(g => g.status === 'FAULTY').length,
  };

  const columns = [
    {
      title: 'GPU ID',
      dataIndex: 'gpuId',
      key: 'gpuId',
      width: 120,
    },
    {
      title: 'VastIns ID',
      dataIndex: 'vastInsId',
      key: 'vastInsId',
      width: 150,
    },
    {
      title: 'IP地址',
      dataIndex: 'ip',
      key: 'ip',
      width: 120,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => getStatusTag(status),
      filters: [
        { text: '活跃', value: 'ACTIVE' },
        { text: '离线', value: 'OFFLINE' },
        { text: '故障', value: 'FAULTY' },
      ],
      onFilter: (value: any, record: GpuNodeInfo) => record.status === value,
    },
    {
      title: '支持任务类型',
      dataIndex: 'supportedTaskTypes',
      key: 'supportedTaskTypes',
      width: 120,
      render: (types: number[]) => types?.join(', ') || '-',
    },
    {
      title: '任务版本',
      key: 'taskVersions',
      width: 200,
      render: (record: GpuNodeInfo) => {
        const clientVersions = record.clientTaskTypeVersions || {};
        const supportedTypes = record.supportedTaskTypes || [];
        
        if (supportedTypes.length === 0) {
          return <span>-</span>;
        }

        // 获取任务类型名称映射
        const getTaskTypeName = (taskType: number) => {
          const taskTypeInfo = taskTypes.find(t => t.type === taskType);
          return taskTypeInfo?.name || `类型${taskType}`;
        };

        // 获取服务器端版本号
        const getServerVersion = (taskType: number) => {
          const versionInfo = taskVersions.find(v => v.taskType === taskType && v.enabled);
          return versionInfo?.version || '-';
        };

        const isExpanded = expandedVersions.has(record.gpuId);
        const displayTypes = isExpanded ? supportedTypes : supportedTypes.slice(0, 1);
        const hasMore = supportedTypes.length > 1;

        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {displayTypes.map((taskType) => {
              const clientVersion = clientVersions[taskType];
              const serverVersion = getServerVersion(taskType);
              const taskTypeName = getTaskTypeName(taskType);
              
              return (
                <div key={taskType} style={{ fontSize: '12px' }}>
                  <Tag color="blue" style={{ marginRight: 4 }}>
                    {taskTypeName}
                  </Tag>
                  <span>
                    客户端: <strong>{clientVersion || '未知'}</strong>
                    {serverVersion && serverVersion !== '-' && (
                      <span style={{ marginLeft: 8, color: '#999' }}>
                        (服务器: {serverVersion})
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
            {hasMore && (
              <Button
                type="link"
                size="small"
                icon={<MoreOutlined />}
                onClick={() => {
                  const newExpanded = new Set(expandedVersions);
                  if (isExpanded) {
                    newExpanded.delete(record.gpuId);
                  } else {
                    newExpanded.add(record.gpuId);
                  }
                  setExpandedVersions(newExpanded);
                }}
                style={{ padding: 0, height: 'auto' }}
              >
                {isExpanded ? '收起' : `更多(${supportedTypes.length - 1})`}
              </Button>
            )}
          </Space>
        );
      },
    },
    {
      title: '优先级',
      dataIndex: 'supportedTaskPriority',
      key: 'supportedTaskPriority',
      width: 100,
      render: (priority: string) => getPriorityTag(priority),
    },
    {
      title: '当前任务',
      key: 'currentTasks',
      width: 100,
      render: (record: GpuNodeInfo) => 
        `${record.currentTasks || 0}/${record.maxConcurrentTasks || 0}`,
    },
    {
      title: '最后心跳',
      dataIndex: 'lastHeartbeat',
      key: 'lastHeartbeat',
      width: 150,
      render: (timestamp: number) => 
        formatTimestampInShanghai(timestamp),
      sorter: (a: GpuNodeInfo, b: GpuNodeInfo) => a.lastHeartbeat - b.lastHeartbeat,
    },
    {
      title: '上个任务完成时间',
      dataIndex: 'lastTaskCompletedAt',
      key: 'lastTaskCompletedAt',
      width: 190,
      render: (timestampFromList: GpuNodeInfo['lastTaskCompletedAt'], record: GpuNodeInfo) => {
        const timestamp = timestampFromList ?? getLastTaskCompletedAt(record);
        const normalizedTimestamp = normalizeTimestamp(timestamp);

        if (!normalizedTimestamp) {
          return <Tag color="default">暂无记录</Tag>;
        }

        const elapsedMs = now - normalizedTimestamp;
        const isOverThreshold = elapsedMs > GPU_LAST_TASK_WARNING_MS;

        return (
          <Space direction="vertical" size={2}>
            <span>{formatTimestampInShanghai(timestamp)}</span>
            <Tag color={isOverThreshold ? 'red' : 'green'}>
              {isOverThreshold ? `超过10分钟（${formatElapsedTime(elapsedMs)}）` : formatElapsedTime(elapsedMs)}
            </Tag>
          </Space>
        );
      },
      sorter: (a: GpuNodeInfo, b: GpuNodeInfo) =>
        (normalizeTimestamp(getLastTaskCompletedAt(a)) || 0) -
        (normalizeTimestamp(getLastTaskCompletedAt(b)) || 0),
    },
    {
      title: '升级状态',
      key: 'upgradeStatus',
      width: 150,
      render: (record: GpuNodeInfo) => {
        // 后端已经在列表接口(/gpus/admin/list)中返回了 needUpgrade 和 upgradeEnabled 字段
        // 直接使用这些字段，如果为 undefined 或 null，则默认为 false
        const needUpgrade = Boolean(record.needUpgrade);
        const upgradeEnabled = Boolean(record.upgradeEnabled);
        return (
          <Space direction="vertical" size="small">
            {needUpgrade ? (
              <Tag color="orange">需要升级</Tag>
            ) : (
              <Tag color="green">无需升级</Tag>
            )}
            {upgradeEnabled ? (
              <Tag color="blue">允许升级</Tag>
            ) : (
              <Tag color="default">禁止升级</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 300,
      render: (record: GpuNodeInfo) => (
        <Space wrap>
          <Button 
            size="small" 
            icon={<HeartOutlined />}
            onClick={() => handleHeartbeat(record.gpuId)}
          >
            心跳
          </Button>
          <Popconfirm
            title="确定要标记此GPU为故障吗？"
            onConfirm={() => handleMarkFaulty(record.gpuId)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              size="small" 
              danger
              icon={<ExclamationCircleOutlined />}
            >
              故障
            </Button>
          </Popconfirm>
          <Button 
            size="small" 
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => handleRecover(record.gpuId)}
          >
            恢复
          </Button>
          {Boolean(record.needUpgrade) && (
            <>
              {!record.upgradeEnabled ? (
                <Popconfirm
                  title="确定要允许此GPU升级吗？"
                  description="允许后，GPU客户端将在下次心跳时执行升级操作"
                  onConfirm={() => handleSetUpgradeEnabled(record.gpuId, true)}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button 
                    size="small" 
                    type="primary"
                    icon={<UpCircleOutlined />}
                  >
                    允许升级
                  </Button>
                </Popconfirm>
              ) : (
                <>
                  <Popconfirm
                    title="确定要禁止此GPU升级吗？"
                    onConfirm={() => handleSetUpgradeEnabled(record.gpuId, false)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button 
                      size="small" 
                      danger
                      icon={<StopOutlined />}
                    >
                      禁止升级
                    </Button>
                  </Popconfirm>
                  <Popconfirm
                    title="确定要清除此GPU的升级状态吗？"
                    description="清除后将恢复默认状态（禁止升级）"
                    onConfirm={() => handleClearUpgradeStatus(record.gpuId)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button 
                      size="small" 
                      icon={<ClearOutlined />}
                    >
                      清除状态
                    </Button>
                  </Popconfirm>
                </>
              )}
            </>
          )}
          <Popconfirm
            title="确定要注销此GPU吗？"
            description="注销后GPU将从系统中移除，此操作不可撤销"
            onConfirm={() => handleUnregister(record.gpuId)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              size="small" 
              danger
              icon={<DeleteOutlined />}
            >
              注销
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ 
        marginBottom: 16, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8
      }}>
        <h1 style={{ margin: 0, fontSize: window.innerWidth <= 768 ? '18px' : '24px' }}>GPU管理</h1>
        <Space wrap>
          <Button 
            icon={<PlusOutlined />} 
            onClick={() => setIsModalVisible(true)}
            size={window.innerWidth <= 768 ? 'small' : 'middle'}
          >
            添加GPU
          </Button>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={refreshData} 
            loading={loading}
            size={window.innerWidth <= 768 ? 'small' : 'middle'}
          >
            刷新
          </Button>
        </Space>
      </div>

      {/* 统计信息 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="总GPU数" value={stats.total} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="活跃GPU" value={stats.active} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="离线GPU" value={stats.offline} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="故障GPU" value={stats.faulty} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
      </Row>

      <Card>
        {/* 搜索和筛选 */}
        <div style={{ 
          marginBottom: 16, 
          display: 'flex', 
          gap: 16, 
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <Input
            placeholder="搜索GPU ID、VastIns ID或IP"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ 
              width: window.innerWidth <= 768 ? '100%' : 300,
              minWidth: window.innerWidth <= 768 ? '100%' : 200
            }}
          />
          <Select
            placeholder="状态筛选"
            style={{ width: window.innerWidth <= 768 ? '100%' : 120 }}
            allowClear
            value={filters.status}
            onChange={(value) => dispatch(setFilters({ status: value }))}
          >
            <Option value="ACTIVE">活跃</Option>
            <Option value="OFFLINE">离线</Option>
            <Option value="FAULTY">故障</Option>
          </Select>
          <Select
            placeholder="任务类型"
            style={{ width: window.innerWidth <= 768 ? '100%' : 220 }}
            allowClear
            showSearch
            optionFilterProp="children"
            value={filters.taskType}
            onChange={(value) => dispatch(setFilters({ taskType: value }))}
          >
            {taskTypes.map((taskType) => (
              <Option key={taskType.type} value={taskType.type}>
                {`${taskType.type} - ${taskType.name || `类型${taskType.type}`}`}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="优先级"
            style={{ width: window.innerWidth <= 768 ? '100%' : 120 }}
            allowClear
            value={filters.priority}
            onChange={(value) => dispatch(setFilters({ priority: value }))}
          >
            <Option value="VIP">VIP</Option>
            <Option value="NORMAL">普通</Option>
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="gpuId"
          rowClassName={(record: GpuNodeInfo) => (
            isLastTaskOlderThanThreshold(record) ? 'gpu-last-task-stale-row' : ''
          )}
          pagination={{
            pageSize: window.innerWidth <= 768 ? 10 : 20,
            showSizeChanger: window.innerWidth > 768,
            showQuickJumper: window.innerWidth > 768,
            showTotal: (total) => `共 ${total} 条记录`,
            size: window.innerWidth <= 768 ? 'small' : 'default'
          }}
          scroll={{ x: window.innerWidth <= 768 ? 1000 : 1400 }}
          size={window.innerWidth <= 768 ? 'small' : 'middle'}
        />
      </Card>

      {/* 添加GPU模态框 */}
      <Modal
        title="添加GPU"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={window.innerWidth <= 768 ? '95%' : 600}
        style={{ top: window.innerWidth <= 768 ? 20 : 100 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleRegister}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="gpuId"
                label="GPU ID"
                rules={[{ required: true, message: '请输入GPU ID' }]}
              >
                <Input placeholder="请输入GPU ID" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="hostname"
                label="主机名"
                rules={[{ required: true, message: '请输入主机名' }]}
              >
                <Input placeholder="请输入主机名" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="ip"
                label="IP地址"
                rules={[{ required: true, message: '请输入IP地址' }]}
              >
                <Input placeholder="请输入IP地址" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="port"
                label="端口"
                rules={[{ required: true, message: '请输入端口' }]}
              >
                <InputNumber placeholder="请输入端口" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="supportedTaskTypes"
                label="支持任务类型"
                rules={[{ required: true, message: '请选择支持的任务类型' }]}
              >
                <Select mode="multiple" placeholder="请选择任务类型">
                  {taskTypes.map((taskType) => (
                    <Option key={taskType.type} value={taskType.type}>
                      {taskType.name || `类型${taskType.type}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="supportedTaskPriority"
                label="支持优先级"
                rules={[{ required: true, message: '请选择支持的优先级' }]}
              >
                <Select placeholder="请选择优先级">
                  <Option value="VIP">VIP</Option>
                  <Option value="NORMAL">普通</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="maxConcurrentTasks"
                label="最大并发任务数"
                rules={[{ required: true, message: '请输入最大并发任务数' }]}
              >
                <InputNumber placeholder="请输入最大并发任务数" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="version"
                label="版本"
                rules={[{ required: true, message: '请输入版本' }]}
              >
                <Input placeholder="请输入版本" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                确定
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GpuManagement;
